import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../../../context/AuthContext';
import { useAdmin } from '../../../hooks/useAdmin';
import { useNavViewer } from '../../../hooks/useNavViewer';
import { useUnreadNotificationCount } from '../../../hooks/useUnreadNotificationCount';
import { formatXp, initials } from '../format';
import { UT_LOGO } from '../primitives';
import { tierGradient, utTier } from '../tiers';
import { UT_TOP_NAV, HOME_NAV } from './navItems';

/**
 * Sticky UT header: logo and wordmark, inline nav from 834px, inbox with
 * unread badge, and the XP chip that opens your own profile.
 */
export const UtHeader: React.FC = () => {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdmin();
  const viewer = useNavViewer();
  const unread = useUnreadNotificationCount();
  const tier = utTier(viewer.rarity, viewer.xp);

  return (
    <header className="sticky top-0 z-40 border-b border-ut-line bg-ut-bg2">
      <div className="mx-auto flex max-w-[1264px] items-center justify-between gap-4 px-4 py-3">
        <Link to="/" className="flex flex-none items-center gap-2" aria-label="WNF home">
          <img src={UT_LOGO} alt="" className="h-[38px] w-auto" />
          <span className="flex flex-col leading-none">
            <span className="font-ut-display text-lg font-extrabold tracking-[0.04em]">WNF</span>
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.14em] text-ut-mut min-[380px]:block">
              Wednesday Night Football
            </span>
          </span>
        </Link>

        <nav aria-label="Main" className="hidden flex-1 justify-center gap-1 ut-md:flex">
          {UT_TOP_NAV.filter((item) => item !== HOME_NAV || !!viewer.playerId).map((item) => {
            const active = item.match(pathname, search);
            return (
              <Link
                key={item.key}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={clsx(
                  'rounded-lg px-3 py-2 font-ut-display text-[15px] font-bold uppercase tracking-[0.08em] transition-colors',
                  active ? 'bg-ut-acc-soft text-ut-acc' : 'text-ut-mut hover:text-ut-ink',
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex flex-none items-center gap-2">
          {(isAdmin || isSuperAdmin) && (
            <Link
              to="/admin"
              className="rounded-lg border border-ut-line px-2.5 py-1.5 font-ut-display text-xs font-bold tracking-[0.1em] text-ut-mut hover:text-ut-ink"
            >
              ADMIN
            </Link>
          )}
          {user ? (
            <>
              <Link
                to="/notifications"
                aria-label={unread > 0 ? `Inbox, ${unread} unread` : 'Inbox'}
                className="relative flex h-9 w-9 items-center justify-center rounded-full bg-ut-card font-ut-display text-[11px] font-bold tracking-[0.06em] text-ut-mut"
              >
                INBOX
                {unread > 0 && (
                  <span className="absolute -right-[3px] -top-[3px] flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] bg-ut-acc px-1 text-[11px] font-extrabold text-ut-acc-ink">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </Link>
              <Link
                to={viewer.playerId ? `/players/${viewer.playerId}` : '/profile'}
                aria-label="Your player card"
                className="flex items-center gap-1.5 rounded-full bg-ut-card py-1 pl-2.5 pr-1"
              >
                <span className="font-ut-display text-sm font-bold text-ut-acc">{formatXp(viewer.xp)} XP</span>
                <span
                  className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-full font-ut-display text-xs font-extrabold"
                  style={{ background: tierGradient(tier), color: tier.ink }}
                >
                  {viewer.photo ? (
                    <img src={viewer.photo} alt="" className="absolute inset-0 h-full w-full object-cover object-top" />
                  ) : (
                    initials(viewer.friendlyName)
                  )}
                </span>
              </Link>
            </>
          ) : (
            <Link
              to="/login"
              className="rounded-full bg-ut-acc px-3.5 py-2 font-ut-display text-sm font-extrabold tracking-[0.06em] text-ut-acc-ink"
            >
              SIGN IN
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
